import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6';
import reactLazyWithRetry from '@fatso83/retry-dynamic-import/react-lazy';
import FaceIcon from '@mui/icons-material/Face';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Divider, Typography } from '@mui/material';
import { BadgeCheckIcon } from '@parkdna/ui/icons/BadgeCheckIcon';
import { Building2Icon } from '@parkdna/ui/icons/Building2Icon';
import { CalendarCogIcon } from '@parkdna/ui/icons/CalendarCogIcon';
import { CameraIcon } from '@parkdna/ui/icons/CameraIcon';
import { CarIcon } from '@parkdna/ui/icons/CarIcon';
import { ChartColumnIncreasingIcon } from '@parkdna/ui/icons/ChartLineIcon';
import { CreditCardIcon } from '@parkdna/ui/icons/CreditCardIcon';
import { FileBarChartIcon } from '@parkdna/ui/icons/FileBarChartIcon';
import { MapIcon } from '@parkdna/ui/icons/MapIcon';
import { MapPinHouseIcon } from '@parkdna/ui/icons/MapPinHouseIcon';
import { ShieldAlertIcon } from '@parkdna/ui/icons/ShieldAlertIcon';
import { TicketIcon } from '@parkdna/ui/icons/TicketIcon';
import { UsersIcon } from '@parkdna/ui/icons/UsersIcon';
import { Role, Subject, useAuth } from 'hooks/auth/useAuth';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Bell,
  Camera,
  Car,
  CreditCard,
  FileTextIcon,
  Landmark,
  LandmarkIcon,
  MapPin,
  Megaphone,
  Monitor,
  Settings,
  ShieldAlert,
  Ticket,
} from 'lucide-react';
import { Suspense, useMemo } from 'react';
import {
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider as RRDRouterProvider,
} from 'react-router-dom';

import ProtectedRoute from '@/components/auth/ProtectedRoute';
import FeatureFlagProtected from '@/components/FeatureFlagProtected';
import { MenuItem } from '@/components/layout/sidebar/SidebarMenuItems';
import TakeTourButton from '@/components/tours/validator/TakeTourButton';
import SidebarMenuItemsContext from '@/contexts/sidebar-menu-items';
import { useEnforcementStatementAccess } from '@/features/enforcement-statements';
import Layout from '@/layouts';
import NotFound from '@/scenes/NotFound';
import RootLayout from '@/scenes/RootLayout';
import RedirectLegacyDispute from '@/scenes/transactions/RedirectLegacyDispute';

const ViolationUploadPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaViolationUploadPage' */ './violation-upload/ViolationUploadPage'
    )
);

const Profile = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaProfilePage' */ './profile')
);
const Dashboard = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaDashboardPage' */ './dashboard')
);
const Transactions = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaTransactionsEnforcementPage' */ './transactions'
    )
);
const EnforcementStatements = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaEnforcementStatementsPage' */ './enforcement-statements'
    )
);
const ParkingSessionsPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaParkingSessionsPage' */ './parking-sessions'
    )
);

const TransactionDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaTransactionDetailPage' */ './transactions/detail/detail'
    )
);

const ValidationsListPage = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaValidationsPage' */ './validations')
);

const SubLocationsListPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaSubLocationsListPage' */ './validations/sublocations'
    )
);

const Reports = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaReportsPage' */ './reports')
);
const ReportSchedulesPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaReportSchedulesPage' */ './reports/schedules'
    )
);
const Agreements = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaAgreementsPage' */ './agreements')
);
const GroupDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaGroupDetailPage' */ './agreements/group-detail'
    )
);
const AgreementDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaAgreementDetailPage' */ './agreements/agreement-detail'
    )
);
const Locations = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaLocationsListPage' */ './locations')
);
const Permits = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaPermitsListPage' */ './permits')
);
const PermitDetailPage = reactLazyWithRetry(
  () =>
    import(/* webpackChunkName: 'parkdnaPermitDetailPage' */ './permits/detail')
);
const UsersManagementPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaUsersManagementPage' */ './org-management'
    )
);

const CameraEvents = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaCameraEventsPage' */ './monitoring-workspace/camera-events'
    )
);
const CamerasDashboard = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaCamerasDashboardPage' */ './monitoring-workspace/cameras/dashboard'
    )
);
const CameraDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaCameraDetailPage' */ './monitoring-workspace/cameras/detail'
    )
);
const CameraAlertsPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaCameraAlertsPage' */ './monitoring-workspace/cameras/alerts'
    )
);
const Customers = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaCustomersPage' */ './customers')
);
const CustomerDetail = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaCustomerDetailPage' */ './customers/detail'
    )
);
const MonitoringWorkspaceViolationsPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaMonitoringWorkspaceViolationsPage' */ './monitoring-workspace/violations'
    )
);
const LocationDetail = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaLocationDetailPage' */ './locations/detail'
    )
);
const ViolationDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaViolationDetailPage' */ './monitoring-workspace/violations/detail'
    )
);

const DevicesPage = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaDevicesPage' */ './devices')
);

const ViolationTriagePage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaViolationTriagePage' */ './monitoring-workspace/violations/triage'
    )
);

const ParkingSessionDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaParkingSessionDetailPage' */ './parking-sessions/detail'
    )
);

const DisputeDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaDisputeDetailPage' */ './parking-sessions/dispute'
    )
);

const SubLocationDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaSublocationDetailPage' */ './validations/sublocations/detail'
    )
);

const BroadcastsListPage = reactLazyWithRetry(
  () =>
    import(/* webpackChunkName: 'parkdnaBroadcastsListPage' */ './broadcasts')
);

const BroadcastComposePage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaBroadcastComposePage' */ './broadcasts/new'
    )
);

const BroadcastDetailPage = reactLazyWithRetry(
  () =>
    import(
      /* webpackChunkName: 'parkdnaBroadcastDetailPage' */ './broadcasts/detail'
    )
);

const SupportPage = reactLazyWithRetry(
  () => import(/* webpackChunkName: 'parkdnaSupportPage' */ './support')
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      {/* Public route — no auth required */}
      <Route
        path="/upload/:token"
        element={
          <Suspense fallback={null}>
            <ViolationUploadPage />
          </Suspense>
        }
      />
      <Route element={<RootLayout />}>
        <Route
          path="*"
          element={<NotFound />}
          handle={{ title: 'Not Found' }}
        />
        <Route element={<Layout.Suspense />}>
          {/* Dashboard */}
          <Route
            element={
              <ProtectedRoute
                permissions={[
                  { subject: Subject.RESERVATIONS, action: 'read' },
                  { subject: Subject.VIOLATIONS, action: 'read' },
                ]}
                oneOf
              />
            }
          >
            <Route
              path="/dashboard"
              element={<Dashboard />}
              handle={{ title: 'Dashboard' }}
            />
          </Route>

          {/* Profile */}
          <Route
            element={
              <Layout.Scene title="Account" subtitle="Manage your account" />
            }
          >
            <Route
              path="/profile"
              element={<Profile />}
              handle={{ title: 'Account' }}
            />
          </Route>

          {/* Organization */}
          <Route element={<ProtectedRoute subject={Subject.USERS} />}>
            <Route
              path="/organization"
              element={
                <Layout.Scene
                  title="Organization Management"
                  subtitle="Manage members of your organization"
                />
              }
            >
              <Route
                path=""
                index
                element={<UsersManagementPage />}
                handle={{ title: 'Organization' }}
              />
            </Route>
          </Route>

          <Route element={<ProtectedRoute subject={Subject.REVENUE} />}>
            <Route
              path="/revenue/enforcement-statements"
              element={<EnforcementStatements />}
              handle={{
                title: 'Enforcement Statements',
                crumb: { label: 'Enforcement Statements', icon: Landmark },
              }}
            />
          </Route>

          {/* Transactions/Payments (+ Disputes queue & detail) */}
          <Route element={<ProtectedRoute subject={Subject.RESERVATIONS} />}>
            <Route
              path="/transactions"
              handle={{ crumb: { label: 'Payments', icon: CreditCard } }}
            >
              <Route
                index
                element={<Transactions />}
                handle={{ title: 'Payments' }}
              />
              {/* Disputes tab + detail require read:disputes (in addition to Payments). */}
              <Route element={<ProtectedRoute subject={Subject.DISPUTES} />}>
                <Route path="disputes" handle={{ crumb: { label: 'Dispute' } }}>
                  <Route
                    index
                    element={
                      <Navigate to="/transactions?tab=disputes" replace />
                    }
                  />
                  <Route
                    path=":disputeId"
                    element={<DisputeDetailPage />}
                    handle={{
                      title: 'Dispute Detail',
                      crumb: {
                        label: (params: { disputeId?: string }) =>
                          params.disputeId || 'Dispute Detail',
                        copyable: true,
                      },
                    }}
                  />
                </Route>
              </Route>
              <Route
                path=":reservationId"
                element={<TransactionDetailPage />}
                handle={{
                  title: 'Payment Detail',
                  crumb: {
                    label: (params) => params.reservationId || 'Payment Detail',
                  },
                }}
              />
            </Route>
          </Route>

          {/* Parking Sessions */}
          <Route
            element={<ProtectedRoute subject={Subject.PARKING_SESSIONS} />}
          >
            <Route
              path="/parking-sessions"
              handle={{ crumb: { label: 'Parking Sessions', icon: Car } }}
            >
              <Route
                index
                element={<ParkingSessionsPage />}
                handle={{ title: 'Parking Sessions' }}
              />
              <Route
                path=":sessionId"
                element={<ParkingSessionDetailPage />}
                handle={{
                  title: 'Parking Session Detail',
                  crumb: {
                    label: (params) => params.sessionId || 'Session Detail',
                    copyable: true,
                  },
                }}
              />
            </Route>
          </Route>

          {/* Legacy dispute URLs → Payments */}
          <Route
            path="/parking-sessions/disputes"
            element={<Navigate to="/transactions?tab=disputes" replace />}
          />
          <Route
            path="/parking-sessions/disputes/:disputeId"
            element={<RedirectLegacyDispute />}
          />

          {/* Validations - Sublocations */}
          <Route path="/validations">
            <Route
              element={<ProtectedRoute subject={Subject.VALIDATION_CONFIGS} />}
            >
              <Route
                path="sublocations"
                handle={{ crumb: { label: 'Sublocations', icon: MapPin } }}
              >
                <Route
                  element={
                    <Layout.Scene
                      title="Sublocations"
                      subtitle="Manage your sublocations"
                    />
                  }
                >
                  <Route
                    index
                    element={<SubLocationsListPage />}
                    handle={{ title: 'Sublocations' }}
                  />
                </Route>
                <Route
                  path=":locationId/:subLocationId"
                  element={<SubLocationDetailPage />}
                  handle={{
                    title: 'Sublocation Detail',
                    crumb: { label: 'Sublocation Detail' },
                  }}
                />
              </Route>

              {/* Validations - List */}
              <Route
                path="list"
                element={
                  <Layout.Scene
                    title="Validations"
                    subtitle="List of validations"
                  />
                }
              >
                <Route
                  index
                  element={<ValidationsListPage />}
                  handle={{ title: 'Validations' }}
                />
              </Route>
            </Route>
          </Route>

          {/* Broadcasts */}
          <Route
            element={
              <ProtectedRoute subject={Subject.COMMUNICATIONS} action="send" />
            }
          >
            <Route
              path="/broadcasts"
              handle={{ crumb: { label: 'Broadcasts', icon: Megaphone } }}
            >
              <Route
                element={
                  <Layout.Scene
                    title="Broadcasts"
                    subtitle="Send SMS to Active Patrons"
                  />
                }
              >
                <Route
                  index
                  element={<BroadcastsListPage />}
                  handle={{ title: 'Broadcasts' }}
                />
              </Route>
              <Route
                path="new"
                element={<BroadcastComposePage />}
                handle={{
                  title: 'New Broadcast',
                  crumb: { label: 'New Broadcast' },
                }}
              />
              <Route
                path=":id"
                element={<BroadcastDetailPage />}
                handle={{
                  title: 'Broadcast Detail',
                  crumb: {
                    label: (params) => params.id || 'Broadcast Detail',
                    copyable: true,
                  },
                }}
              />
            </Route>
          </Route>

          {/* Support */}
          <Route
            path="/support"
            element={<SupportPage />}
            handle={{ title: 'Support' }}
          />

          {/* Reports */}
          <Route
            element={
              <ProtectedRoute subject={Subject.REPORTS} action="generate" />
            }
          >
            <Route path="/reports">
              <Route
                element={
                  <Layout.Scene
                    title="Reports"
                    subtitle="Generate Detailed Reports"
                  />
                }
              >
                <Route
                  index
                  element={<Reports />}
                  handle={{ title: 'Reports' }}
                />
              </Route>
              <Route
                element={
                  <Layout.Scene
                    title="Report Schedules"
                    subtitle="Manage scheduled report delivery"
                  />
                }
              >
                <Route
                  path="schedules"
                  element={<ReportSchedulesPage />}
                  handle={{ title: 'Report Schedules' }}
                />
              </Route>
            </Route>
          </Route>

          {/* Locations */}
          <Route element={<ProtectedRoute subject={Subject.LOCATIONS} />}>
            <Route
              path="/locations"
              handle={{
                crumb: { label: 'Locations', icon: PlaceOutlinedIcon },
              }}
            >
              <Route
                index
                element={<Locations />}
                handle={{ title: 'Locations' }}
              />
              <Route
                path=":id"
                element={<LocationDetail />}
                handle={{
                  title: 'Location Detail',
                  crumb: { label: 'Location Detail' },
                }}
              />
            </Route>
          </Route>

          {/* Devices */}
          <Route element={<ProtectedRoute subject={Subject.DEVICES} />}>
            <Route
              path="/devices"
              element={
                <FeatureFlagProtected flagName="accessPointSchedulingEnabled">
                  <DevicesPage />
                </FeatureFlagProtected>
              }
              handle={{ title: 'Devices' }}
            />
          </Route>

          {/* Permits */}
          <Route element={<ProtectedRoute subject={Subject.PERMITS} />}>
            <Route
              path="/permits"
              handle={{ crumb: { label: 'Permits', icon: Ticket } }}
            >
              <Route
                index
                element={<Permits />}
                handle={{ title: 'Permits' }}
              />
              <Route
                path=":permitId"
                element={<PermitDetailPage />}
                handle={{
                  title: 'Permit Detail',
                  crumb: {
                    label: (params) => params.permitId || 'Permit Detail',
                    copyable: true,
                  },
                }}
              />
            </Route>
          </Route>

          <Route element={<ProtectedRoute subject={Subject.MONTHLY_PARKERS} />}>
            <Route
              path="/agreements"
              handle={{
                crumb: { label: 'Monthly Parkers', icon: CalendarCogIcon },
              }}
            >
              <Route
                index
                element={
                  <FeatureFlagProtected flagName="monthlyParkingV2">
                    <Agreements />
                  </FeatureFlagProtected>
                }
                handle={{ title: 'Monthly Parkers' }}
              />
              <Route
                path="groups/:groupId"
                element={
                  <FeatureFlagProtected flagName="monthlyParkingV2">
                    <GroupDetailPage />
                  </FeatureFlagProtected>
                }
                handle={{
                  title: 'Monthly Parker Group',
                  crumb: { label: 'Monthly Parker Group' },
                }}
              />
              <Route
                path=":agreementId"
                element={
                  <FeatureFlagProtected flagName="monthlyParkingV2">
                    <AgreementDetailPage />
                  </FeatureFlagProtected>
                }
                handle={{
                  title: 'Monthly Parker',
                  crumb: { label: 'Monthly Parker' },
                }}
              />
            </Route>
          </Route>

          {/* Customers */}
          <Route element={<ProtectedRoute subject={Subject.CUSTOMERS} />}>
            <Route
              path="/customers"
              handle={{ crumb: { label: 'Customers', icon: FaceIcon } }}
            >
              <Route
                element={
                  <Layout.Scene
                    title="Customers"
                    subtitle="List of customers"
                  />
                }
              >
                <Route
                  index
                  element={<Customers />}
                  handle={{ title: 'Customers' }}
                />
              </Route>
              <Route
                path=":customerId"
                element={<CustomerDetail />}
                handle={{
                  title: 'Customer Detail',
                  crumb: { label: 'Customer Detail' },
                }}
              />
            </Route>
          </Route>

          {/* Monitoring Workspace */}
          <Route path="/monitoring/workspace">
            {/* Violations */}
            <Route element={<ProtectedRoute subject={Subject.VIOLATIONS} />}>
              <Route
                path="violations"
                handle={{
                  title: 'Violations',
                  crumb: { label: 'Violations', icon: ShieldAlert },
                }}
              >
                <Route index element={<MonitoringWorkspaceViolationsPage />} />
                <Route
                  element={
                    <ProtectedRoute subject={Subject.VIOLATION_TRIAGE} />
                  }
                >
                  <Route
                    path="triage"
                    element={<ViolationTriagePage />}
                    handle={{
                      title: 'Violation Triage',
                      crumb: { label: 'Triage' },
                    }}
                  />
                </Route>
                <Route
                  path=":violationId"
                  element={<ViolationDetailPage />}
                  handle={{
                    title: 'Violation Detail',
                    crumb: {
                      label: (params) =>
                        params.violationId || 'Violation Detail',
                      copyable: true,
                    },
                  }}
                />
              </Route>
            </Route>

            {/* Cameras */}
            <Route
              path="cameras"
              handle={{ crumb: { label: 'Cameras', icon: Camera } }}
            >
              <Route
                element={<ProtectedRoute subject={Subject.CAMERA_FLEET} />}
              >
                <Route
                  index
                  element={<CamerasDashboard />}
                  handle={{ title: 'Overview' }}
                />
                <Route
                  path=":serialNumber"
                  element={<CameraDetailPage />}
                  handle={{
                    title: 'Camera Detail',
                    crumb: {
                      label: 'Camera Detail',
                      copyable: true,
                    },
                  }}
                />
                <Route
                  path="alerts"
                  element={<CameraAlertsPage />}
                  handle={{ title: 'Camera Alerts' }}
                />
              </Route>
              <Route
                element={<ProtectedRoute subject={Subject.CAMERA_EVENTS} />}
              >
                <Route
                  path="events"
                  element={<CameraEvents />}
                  handle={{ title: 'Camera Events' }}
                />
              </Route>
            </Route>

            {/* Redirect old camera-events URL */}
            <Route
              path="camera-events"
              element={
                <Navigate to="/monitoring/workspace/cameras/events" replace />
              }
            />
          </Route>
        </Route>
      </Route>
    </Route>
  )
);

export default function RouterProvider() {
  const { hasPermission, hasRole } = useAuth();
  const { canRead: canReadEnforcementStatements } =
    useEnforcementStatementAccess();
  const flags = useFlags();
  const isMonthlyParkingV2 = flags['monthlyParkingV2'] === true;

  // TODO(gleb/perf): lazy load icons
  const sidebarMenuItems: MenuItem[] = useMemo(
    () => [
      {
        title: 'Onboarding tour',
        type: 'other',
        element: <TakeTourButton />,
        isHidden: !hasRole(Role.Validator),
      },
      {
        title: 'Dashboard',
        to: '/dashboard',
        iconComponent: ChartColumnIncreasingIcon,
        type: 'menu-item',
        isHidden:
          !hasPermission(Subject.RESERVATIONS, 'read') &&
          !hasPermission(Subject.VIOLATIONS, 'read'),
      },
      {
        type: 'other',
        element: (
          <Divider component="div" role="presentation" sx={{ py: 1 }}>
            <Typography>Data</Typography>
          </Divider>
        ),
        isHidden: false,
      },
      {
        title: 'Payments',
        to: '/transactions',
        iconComponent: CreditCardIcon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.RESERVATIONS, 'read'),
      },
      {
        title: 'Parking Sessions',
        to: '/parking-sessions',
        iconComponent: CarIcon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.PARKING_SESSIONS, 'read'),
      },
      {
        title: 'Sublocations',
        to: '/validations/sublocations',
        iconComponent: MapPinHouseIcon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.VALIDATION_CONFIGS, 'read'),
        dataCy: 'sidebar-menu-item-sublocations',
        dataTestId: 'sidebar-menu-item-sublocations',
      },
      {
        title: 'Validations',
        to: '/validations/list',
        iconComponent: BadgeCheckIcon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.VALIDATION_CONFIGS, 'read'),
        dataCy: 'sidebar-menu-item-validations',
        dataTestId: 'sidebar-menu-item-validations',
      },
      {
        title: 'Reports',
        to: '/reports',
        iconComponent: FileBarChartIcon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.REPORTS, 'generate'),
        dataCy: 'sidebar-menu-item-reports',
        dataTestId: 'sidebar-menu-item-reports',
      },
      {
        title: 'Permits',
        to: '/permits',
        iconComponent: TicketIcon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.PERMITS, 'read'),
      },
      ...(isMonthlyParkingV2
        ? hasPermission(Subject.MONTHLY_PARKERS, 'read')
          ? [
              {
                title: 'Monthly Parkers',
                to: '/agreements',
                iconComponent: CalendarCogIcon,
                type: 'menu-item' as const,
              },
            ]
          : [
              {
                title: 'Monthly Parkers',
                href: `${import.meta.env.VITE_PAYMENTS_APP_URL}/customer-portal`,
                iconComponent: CalendarCogIcon,
                type: 'external-link' as const,
                dataCy: 'sidebar-menu-item-monthly-parkers-customer-portal',
                dataTestId: 'sidebar-menu-item-monthly-parkers-customer-portal',
                hint: {
                  message:
                    'Click here to go to the Monthly Parking Portal to manage your monthly subscribers.',
                  storageKey:
                    'sidebar-hint-dismissed:monthly-parkers-customer-portal',
                },
              },
            ]
        : []),
      {
        title: 'Violations',
        to: '/monitoring/workspace/violations',
        iconComponent: ShieldAlertIcon,
        type: 'menu-item',
        dataCy: 'sidebar-menu-item-monitoring-workspace-violations',
        isHidden: !hasPermission(Subject.VIOLATIONS, 'read'),
      },
      {
        title: 'Broadcasts',
        to: '/broadcasts',
        iconComponent: Megaphone,
        type: 'menu-item',
        dataCy: 'sidebar-menu-item-broadcasts',
        dataTestId: 'sidebar-menu-item-broadcasts',
        isHidden: !hasPermission(Subject.COMMUNICATIONS, 'send'),
      },
      {
        title: 'Devices',
        to: '/devices',
        iconComponent: Monitor,
        type: 'menu-item',
        dataCy: 'sidebar-menu-item-devices',
        dataTestId: 'sidebar-menu-item-devices',
        isHidden:
          !hasPermission(Subject.DEVICES, 'read') ||
          !flags.accessPointSchedulingEnabled,
      },
      ...(hasPermission(Subject.CAMERA_FLEET, 'read')
        ? [
            {
              title: 'Cameras',
              iconComponent: CameraIcon,
              type: 'sub-menu' as const,
              items: [
                {
                  title: 'Manage',
                  to: '/monitoring/workspace/cameras',
                  iconComponent: Settings,
                  type: 'menu-item' as const,
                  end: true,
                },
                {
                  title: 'Events',
                  to: '/monitoring/workspace/cameras/events',
                  iconComponent: CarIcon,
                  type: 'menu-item' as const,
                  isHidden: !hasPermission(Subject.CAMERA_EVENTS, 'read'),
                },
                {
                  title: 'Alerts',
                  to: '/monitoring/workspace/cameras/alerts',
                  iconComponent: Bell,
                  type: 'menu-item' as const,
                },
              ],
            },
          ]
        : [
            {
              title: 'Camera events',
              to: '/monitoring/workspace/cameras/events',
              iconComponent: CameraIcon,
              type: 'menu-item' as const,
              isHidden: !hasPermission(Subject.CAMERA_EVENTS, 'read'),
            },
          ]),
      ...(canReadEnforcementStatements
        ? [
            {
              title: 'Revenue',
              iconComponent: LandmarkIcon,
              type: 'sub-menu' as const,
              items: [
                {
                  title: 'Enforcement',
                  to: '/revenue/enforcement-statements',
                  iconComponent: FileTextIcon,
                  type: 'menu-item' as const,
                },
              ],
            },
          ]
        : []),
      {
        type: 'other',
        element: (
          <Divider component="div" role="presentation" sx={{ py: 1 }}>
            <Typography>Settings</Typography>
          </Divider>
        ),
        isHidden: !(
          hasPermission(Subject.LOCATIONS, 'read') ||
          hasPermission(Subject.CUSTOMERS, 'read') ||
          hasPermission(Subject.USERS, 'read')
        ),
      },
      {
        title: 'Organization',
        to: '/organization',
        iconComponent: Building2Icon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.USERS, 'read'),
      },
      {
        title: 'Customers',
        to: '/customers',
        iconComponent: UsersIcon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.CUSTOMERS, 'read'),
        dataCy: 'sidebar-menu-item-customers',
      },
      {
        title: 'Locations',
        to: '/locations',
        iconComponent: MapIcon,
        type: 'menu-item',
        isHidden: !hasPermission(Subject.LOCATIONS, 'read'),
      },
    ],
    [
      hasPermission,
      hasRole,
      canReadEnforcementStatements,
      isMonthlyParkingV2,
      flags.accessPointSchedulingEnabled,
    ]
  );

  return (
    <SidebarMenuItemsContext.Provider value={sidebarMenuItems}>
      <RRDRouterProvider router={router} />
    </SidebarMenuItemsContext.Provider>
  );
}
